// @givmo/app-admin — the match-approvals table (+ its toolbar, columns, grouped-row cell, and the
// grouped row's disclosure panel).
//
// The primary recurring action surface of the whole portal, and since the approvals split (EP-DES1 §1)
// it lives on `/approvals` rather than the dashboard. It moved as a COMPONENT with its whole
// `data-testid` vocabulary intact — including the now-oddly-named `dashboard-approvals` table id.
// A testid is a wire contract pinned by blocking Playwright specs, so renaming one to match its new
// address would turn a one-line navigation change in e2e into a three-spec selector rewrite. The ugly
// name is the cheap side of that trade.
//
// ★ THIS SECTION RENDERS THE TRIGGERS FOR A MONEY-MOVING PATH BUT OWNS NONE OF IT. The charge dialog,
// its idempotency holder, the reject mutation, and every charge-safety guard stay in
// <ApproveMatchDialog> / `useMatchApprovals`; this section receives `onApprove` / `onDecline` /
// `onApproveAll` callbacks and the already-derived row set. Nothing here decides whether money moves —
// do not move charge logic in.
//
// ROW MODEL (07-CUT0 ①): a grouped (ETF/fundraiser) request renders as ONE row named for the group, and
// its Approve/Decline drive the GROUPED routes over the complete member set (the single routes 400 on
// grouped posts). `rowTestId` is therefore the match id for a single row and the request's
// `request_id` for a grouped one (EP-WEB38; the shared `post_id` before it). The blocking e2e specs
// read a row's id off the page (`firstPendingApprovalRowId`), never off a post.

import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { Check, UserRound, X } from 'lucide-react';
import {
  BADGE_TONE,
  charityCountLabel,
  formatApprovalUsd,
  groupRowTitle,
  type GroupedApprovalRow,
  type MatchApprovalRow,
} from '../dashboard/dashboard-decode';
import {
  CharitiesToggle,
  CollectionCharities,
  CollectionMark,
} from '../dashboard/collection-charities';
import { CharityCell } from '../dashboard/sections/charity-cell';
import { errorMessage } from '../dashboard/sections/section-status';

/** Where a collection row's charities open: its disclosure row (EP-WEB40). */
const charitiesId = (row: MatchApprovalRow) => `approval-charities-${row.id}`;

// ── A charity-collection gift's identity cell (EP-WEB40): the collection's mark and its words, and
//    "{N} charities ›", which opens the row's charities — the control that says it opens them
//    (D-CAM-113). No kind badge: the words and the count already say what it is. ──────────────────
function CollectionCell({
  row,
  expanded,
  onToggle,
}: {
  row: GroupedApprovalRow & { collection: NonNullable<GroupedApprovalRow['collection']> };
  expanded: boolean;
  onToggle: () => void;
}) {
  const title = groupRowTitle(row);
  return (
    <div className="flex min-w-0 items-center gap-10">
      <CollectionMark label={row.collection} size="sm" />
      <div className="flex min-w-0 flex-col items-start gap-2">
        <span className="truncate font-w600 text-text-strong">{title}</span>
        <CharitiesToggle
          count={row.members.length}
          title={title}
          expanded={expanded}
          onToggle={onToggle}
          controls={charitiesId(row)}
          testId={`approval-charities-toggle-${row.id}`}
        />
      </div>
    </div>
  );
}

// ── Grouped-request identity cell (07-CUT0 ①): the group's name + a kind/count badge — the table twin
//    of the Flutter webapp's "requested a match to <ETF>" tile head. The member charities are NOT a
//    `title` tooltip: they are disclosed by the row's expander (see `GroupedMembers`), because a
//    tooltip is invisible to keyboard and screen-reader users and this control charges a card. ──────
function GroupCell({ row }: { row: GroupedApprovalRow }) {
  const title = groupRowTitle(row);
  const countLabel = charityCountLabel(row.members.length);
  // When the title already IS the count fallback, the badge carries only the kind (if known).
  const badge =
    row.groupName !== null
      ? row.groupKindLabel !== null
        ? `${row.groupKindLabel} · ${countLabel}`
        : countLabel
      : row.groupKindLabel;
  return (
    <div className="flex min-w-0 items-center gap-10">
      <Avatar name={title} size="sm" />
      <div className="flex min-w-0 flex-col items-start gap-3">
        <span className="truncate font-w600 text-text-strong">{title}</span>
        {badge !== null && badge.length > 0 ? (
          <Badge tone="neutral" size="sm">
            {badge}
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

// ── The Employee cell's mark for an employee who hid their name (EP-WEB39): the avatar's circle
//    holds a person glyph, never initials — "An anonymous employee" would read as someone's "AE".
//    Decorative, like the initials it replaces: the name beside it says who asked. ────────────────
function AnonymousAvatar() {
  return (
    <span
      aria-hidden
      className="inline-flex size-32 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700"
    >
      <Icon icon={UserRound} size={16} aria-hidden />
    </span>
  );
}

// ── The grouped row's DISCLOSURE panel (spec §5 hygiene / D-EP-18): every member charity — with its
//    matched slice once the request is approved. A WAITING request names the charities only: its
//    legs' amounts are slices of the employee's gift, which an admin is never shown (D-EP-56), and
//    the match's own split is fixed only when it is charged. This replaces the native `title` tooltip
//    the surface grew on its own — the whole point is that "show the pair" means show the WHOLE pair,
//    reachable by keyboard and announced by a screen reader. Rendered inside <DataTable
//    renderExpanded>, which owns the `<button aria-expanded aria-controls>` toggle and the detail row
//    it points at. ────────────────────────────────────────────────────────────────────────────────────
function GroupedMembers({ row }: { row: GroupedApprovalRow }) {
  return (
    <div className="flex flex-col gap-8" {...testIdProp(`approval-members-${row.id}`)}>
      <p className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
        {`Splits across ${charityCountLabel(row.members.length)}`}
      </p>
      <ul className="flex flex-col gap-4">
        {row.members.map((member, i) => (
          <li
            // Two legs of one group CAN name the same charity, so the index is part of the key.
            key={`${member.charityName}-${i}`}
            className="flex items-baseline justify-between gap-16 font-sans text-s13 tracking-givmo"
          >
            <span className="min-w-0 truncate text-text">{member.charityName}</span>
            {member.amount !== null && (
              <span className="shrink-0 tabular-nums text-text-secondary">{member.amount}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── The Amount cell (D-EP-56): what the company matches — a waiting request's match now, a decided
//    one's matched amount, or "—" — never the employee's gift. A waiting request with no budget left
//    says so on a second line, kept whole: where the table is narrow it scrolls sideways rather than
//    break the note. ──────────────────────────────────────────────────────────────────────────────
function AmountCell({ row }: { row: MatchApprovalRow }) {
  if (row.amountNote === null) return row.amount;
  return (
    <span className="inline-flex flex-col items-end gap-2">
      <span>{row.amount}</span>
      <span
        className="whitespace-nowrap font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
        {...testIdProp(`approval-amount-note-${row.id}`)}
      >
        {row.amountNote}
      </span>
    </span>
  );
}

/** Column set for the approvals table. Owner-gated: the actions column is appended only for managers. */
function buildApprovalColumns(params: {
  canManage: boolean;
  /** A row whose reject is in flight (its buttons disable); null when idle. */
  rejectingId: string | null;
  onApprove: (row: MatchApprovalRow) => void;
  onDecline: (row: MatchApprovalRow) => void;
  /** The rows whose disclosure is open, and the toggle a collection row's "{N} charities ›" drives. */
  expandedKeys: readonly string[];
  onToggleExpanded: (id: string) => void;
}): DataTableColumn<MatchApprovalRow>[] {
  const { canManage, rejectingId, onApprove, onDecline, expandedKeys, onToggleExpanded } = params;
  const base: DataTableColumn<MatchApprovalRow>[] = [
    {
      id: 'employee',
      header: 'Employee',
      cell: (r) => (
        <div className="flex items-center gap-10">
          {r.anonymous ? (
            <AnonymousAvatar />
          ) : (
            <Avatar src={r.employeeLogoUrl} name={r.employeeName} size="sm" />
          )}
          <span className="font-w600 text-text-strong">{r.employeeName}</span>
        </div>
      ),
    },
    {
      id: 'charity',
      header: 'Charity',
      cell: (r) => {
        if (r.kind !== 'grouped') return <CharityCell charity={r.charity} />;
        const { collection } = r;
        if (collection === null) return <GroupCell row={r} />;
        return (
          <CollectionCell
            row={{ ...r, collection }}
            expanded={expandedKeys.includes(r.id)}
            onToggle={() => onToggleExpanded(r.id)}
          />
        );
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: (r) => <AmountCell row={r} />,
      align: 'right',
      numeric: true,
      width: '120px',
    },
    {
      id: 'requested',
      header: 'Requested',
      cell: (r) => <span className="text-text-tertiary">{r.requestedAt}</span>,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      // Cancelled and an unrecognized status share the neutral tone: neither is a decision, so
      // neither may borrow Rejected's red (CAM-R33). The label names an unknown wire value verbatim.
      cell: (r) => (
        <Badge tone={BADGE_TONE[r.status]} dot>
          {r.statusLabel}
        </Badge>
      ),
      width: '120px',
    },
  ];

  if (!canManage) return base;

  return [
    ...base,
    {
      id: 'actions',
      header: '',
      align: 'right',
      width: '210px',
      // Pending rows carry VISIBLE Approve + Decline buttons (07-CUT0 ② — the primary actions an
      // admin takes on this entire site, never behind a kebab). Approve opens the money-moving
      // charge dialog; Decline opens a non-charging reject confirm. A row with no match to approve
      // (its budget used up, or no figure on the wire) keeps Approve DISABLED — we never open a
      // charge we can't price — and stays declinable. Both disable while the row's reject is in
      // flight. Resolved rows render an EMPTY cell (no "—").
      cell: (r) =>
        r.status === 'Pending' ? (
          <div className="flex justify-end gap-8" {...testIdProp(`approval-row-actions-${r.id}`)}>
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              leadingIcon={<Icon icon={Check} size={14} aria-hidden />}
              disabled={r.amountValue === null || rejectingId === r.id}
              onClick={() => onApprove(r)}
              testId={`approval-approve-${r.id}`}
            >
              Approve
            </Button>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              leadingIcon={<Icon icon={X} size={14} aria-hidden />}
              disabled={rejectingId === r.id}
              onClick={() => onDecline(r)}
              testId={`approval-decline-${r.id}`}
            >
              Decline
            </Button>
          </div>
        ) : null,
    },
  ];
}

export function ApprovalsTable({
  rows,
  canManage,
  rejectingId,
  onApprove,
  onDecline,
  pendingCount,
  canApproveAll,
  approvalTotal,
  onApproveAll,
  loading,
  isError,
  error,
  onRetry,
  truncated,
  listLoadedAt,
  controls,
  footer,
  emptyState,
}: {
  rows: MatchApprovalRow[];
  canManage: boolean;
  rejectingId: string | null;
  onApprove: (row: MatchApprovalRow) => void;
  onDecline: (row: MatchApprovalRow) => void;
  pendingCount: number;
  canApproveAll: boolean;
  approvalTotal: number | null;
  onApproveAll: () => void;
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  truncated: boolean;
  /** When the rows were read: an opened collection row reads its shares no earlier (EP-WEB40). */
  listLoadedAt: number;
  /** The queue's search + filter controls, rendered in the toolbar beside Approve-all. */
  controls?: ReactNode;
  /** Overrides the default row-count line (the queue supplies a paginated range instead). */
  footer?: ReactNode;
  /** Overrides the default empty state (the queue distinguishes "nothing pending" from "no match"). */
  emptyState?: ReactNode;
}) {
  // The open disclosures, held here so a collection row's "{N} charities ›" opens the same one its
  // leading toggle does (EP-WEB40).
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const toggleExpanded = useCallback(
    (id: string) =>
      setExpandedKeys((keys) =>
        keys.includes(id) ? keys.filter((key) => key !== id) : [...keys, id],
      ),
    [],
  );

  // Memoized so rows skip re-render on unrelated parent state (a sibling section refetching, the
  // charge dialog opening). The deps are exactly what the columns close over, so a real change —
  // a reject going in flight, the owner gate flipping, a disclosure opening — still rebuilds them.
  const columns = useMemo(
    () =>
      buildApprovalColumns({
        canManage,
        rejectingId,
        onApprove,
        onDecline,
        expandedKeys,
        onToggleExpanded: toggleExpanded,
      }),
    [canManage, rejectingId, onApprove, onDecline, expandedKeys, toggleExpanded],
  );

  // `renderExpanded` reaches the memoized row as a prop, so an inline arrow would re-render every
  // row on every parent render. It closes over the list's load alone, which changes only when the
  // rows are read again, and then the rows are new anyway.
  const renderExpanded = useCallback(
    (r: MatchApprovalRow) => {
      if (r.kind !== 'grouped') return null;
      if (r.collection === null) return <GroupedMembers row={r} />;
      return (
        <CollectionCharities
          id={charitiesId(r)}
          request={{
            matchIds: r.matchIds,
            status: r.status,
            members: r.members,
            statedCents: r.figure?.kind === 'available' ? r.figure.cents : null,
          }}
          explainChange
          since={listLoadedAt}
          testId={charitiesId(r)}
        />
      );
    },
    [listLoadedAt],
  );

  return (
    <DataTable
      testId="dashboard-approvals"
      density="comfortable"
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      rowTestId={(r) => `approval-row-${r.id}`}
      // Only a grouped request has anything to disclose; a single-charity row gets no toggle.
      renderExpanded={renderExpanded}
      expandedKeys={expandedKeys}
      onExpandedChange={setExpandedKeys}
      expandLabel={(r) =>
        r.kind === 'grouped'
          ? `Show the ${charityCountLabel(r.members.length)} in ${groupRowTitle(r)}`
          : 'Show details'
      }
      // DELIBERATELY outside the `approval-row-` namespace: e2e selects rows with
      // `[data-testid^="approval-row-"]`, so a derived `approval-row-<id>-expand` would make every
      // grouped row count twice against a pinned row count.
      expandTestId={(r) => `approval-expand-${r.id}`}
      loading={loading}
      error={isError ? errorMessage(error, 'Failed to load match approvals.') : undefined}
      onRetry={onRetry}
      toolbar={
        <>
          <div className="flex items-center gap-8">
            <h3 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Match approvals
            </h3>
            <Badge tone="warning">{pendingCount} waiting</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-8">
            {controls}
            {/* Owner-gated + inert when no waiting request has a match left to approve. The label
                carries the batch's total — each unit at what approving them all matches for it — so
                the amount is visible before the charge. */}
            {canManage && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={!canApproveAll}
                onClick={onApproveAll}
                testId="dashboard-approve-all"
              >
                {canApproveAll && approvalTotal !== null
                  ? `Approve all (${formatApprovalUsd(approvalTotal)})`
                  : 'Approve all'}
              </Button>
            )}
          </div>
        </>
      }
      emptyState={
        emptyState ?? (
          <EmptyState
            icon={<Icon icon={Check} size={20} aria-hidden />}
            title="No pending approvals"
            description="Match requests from your employees will appear here for review."
          />
        )
      }
      footer={
        footer ??
        (rows.length > 0 ? (
          <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
            {truncated
              ? `Showing the ${rows.length} most recent requests`
              : `Showing all ${rows.length} requests`}
          </span>
        ) : undefined)
      }
    />
  );
}
